import React, { useEffect, useState, useRef } from 'react';
import { ClearSocketMessageHandler, ConnectWebSocketForOverlay, DisconnectWebSocket, SetSocketMessageHandler } from './../../../../utilities/BL/Zones';
import { GetConfig } from './../../../../utilities/BL/Dinosaur';

export const DinosaurScreen = ({ screenId = 1, isStandalone = true, payload = null }) => {
    const getMediaUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http://') || path.startsWith('https://')) return path;
        const apiBase = (process.env.REACT_APP_API_URL || '').replace('/api/', '/').replace('/api', '/');
        return `${apiBase}${path}`;
    };

    const [connectionStatus, setConnectionStatus] = useState('idle');
    const [config, setConfig] = useState(null);
    const [theme, setTheme] = useState(null);
    
    // Estados del juego
    const [gameState, setGameState] = useState('waiting'); // waiting, playing, gameover
    const [score, setScore] = useState(0);
    const [hiScore, setHiScore] = useState(191);
    const [currentPlayerName, setCurrentPlayerName] = useState('');

    const [characterY, setCharacterY] = useState(0);
    const [obstacles, setObstacles] = useState([]);

    // Refs para lógica en frame loop
    const gameStateRef = useRef('waiting');
    const characterYRef = useRef(0);
    const isJumpingRef = useRef(false);
    const jumpVelocityRef = useRef(0);
    const scoreRef = useRef(0);
    const obstaclesRef = useRef([]);
    const animationFrameId = useRef(null);

    // Reloj interno del modo por tiempo
    const timeLimitRef = useRef(30);
    const timeStartRef = useRef(0);

    // Ajustes físicos configurables
    const gravity = 0.65;
    const jumpPower = 14;
    const gameWidth = 800;
    const gameHeight = 300;
    const groundHeight = 30;
    const playerX = 50;
    const playerSize = 75;

    useEffect(() => {
        loadConfig();
        if (isStandalone) {
            connectWS();
            window.addEventListener('keydown', handleKeyDown);
        }

        const localHi = localStorage.getItem('dinosaur_hi_score');
        if (localHi) setHiScore(Number(localHi));

        return () => {
            if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current);
            if (isStandalone) {
                window.removeEventListener('keydown', handleKeyDown);
                ClearSocketMessageHandler();
                DisconnectWebSocket();
            }
        };
    }, [screenId]);

    useEffect(() => {
        if (!isStandalone && payload) {
            handleIncomingMessage(payload);
        }
    }, [payload, isStandalone]);

    const loadConfig = async () => {
        const res = await GetConfig(screenId);
        if (res.success && res.data) {
            setConfig(res.data);
            if (res.data.themeId) {
                fetchTheme(res.data.themeId);
            }
        }
    };

    const fetchTheme = async (themeId) => {
        const response = await fetch(`${process.env.REACT_APP_API_URL}Dinosaur/GetTheme/${themeId}`);
        const data = await response.json();
        if (data.success) {
            setTheme(data.data);
        }
    };

    const connectWS = async () => {
        const overlayType = `dinosaur:screen:${screenId}`;
        await ConnectWebSocketForOverlay(
            overlayType,
            (status) => setConnectionStatus(status),
            (toastMsg) => console.log(`[Dinosaur WS] ${toastMsg}`)
        );
        SetSocketMessageHandler(handleIncomingMessage);
    };

    const handleIncomingMessage = (message) => {
        if (!message || !message.type) return;

        const type = message.type.toLowerCase();
        const msgData = message.data || message.payload || {};

        switch (type) {
            case 'dinosaur.prepare':
                loadConfig();
                resetGame();
                break;
            case 'dinosaur.reset':
                loadConfig();
                resetGame();
                break;
            case 'dinosaur.start':
                loadConfig();
                startGame(msgData.playerName || 'Jugador', msgData.baseSpeed || 8, msgData.playMode || 1, msgData.playDuration || 30);
                break;
            case 'dinosaur.jump':
                triggerJump();
                break;
            case 'dinosaur.gameover':
                // Detener juego forzado desde el admin (STOP)
                if (gameStateRef.current === 'playing') {
                    handleGameOver(false); // Declara fin de partida sin victoria
                }
                break;
            default:
                break;
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            if (gameStateRef.current === 'waiting' || gameStateRef.current === 'gameover') {
                startGame('Botón Antiestrés', config?.baseSpeed || 8, config?.playMode || 1, config?.playDuration || 30);
            } else {
                triggerJump();
            }
        }
    };

    const resetGame = () => {
        if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current);
        setGameState('waiting');
        gameStateRef.current = 'waiting';
        setScore(0);
        scoreRef.current = 0;
        setCharacterY(0);
        characterYRef.current = 0;
        isJumpingRef.current = false;
        jumpVelocityRef.current = 0;
        setObstacles([]);
        obstaclesRef.current = [];
    };

    const startGame = (playerName, speed, playMode, playDuration) => {
        resetGame();
        setCurrentPlayerName(playerName);
        setGameState('playing');
        gameStateRef.current = 'playing';

        timeLimitRef.current = playDuration;
        timeStartRef.current = performance.now();

        let lastObstacleSpawn = 0;

        const loop = (time) => {
            if (gameStateRef.current !== 'playing') return;

            // 1. Verificar Modo de Tiempo Límite
            if (playMode === 2) {
                const elapsedSeconds = (time - timeStartRef.current) / 1000;
                if (elapsedSeconds >= timeLimitRef.current) {
                    handleGameOver(true); // Superó el tiempo límite = Victoria
                    return;
                }
            }

            // 2. Aumentar Puntuación
            scoreRef.current += 0.15;
            setScore(Math.floor(scoreRef.current));

            // 3. Lógica del Salto
            if (isJumpingRef.current) {
                characterYRef.current += jumpVelocityRef.current;
                jumpVelocityRef.current -= gravity;

                if (characterYRef.current <= 0) {
                    characterYRef.current = 0;
                    isJumpingRef.current = false;
                    jumpVelocityRef.current = 0;
                }
                setCharacterY(characterYRef.current);
            }

            // 4. Mover Obstáculos y verificar Colisión
            const speedMultiplier = 1 + Math.floor(scoreRef.current / 300) * 0.1;
            const currentSpeed = speed * speedMultiplier;

            let currentObstacles = [...obstaclesRef.current];
            currentObstacles = currentObstacles.map(obs => ({
                ...obs,
                x: obs.x - currentSpeed
            })).filter(obs => obs.x > -100);

            // Spawnear obstáculo
            if (time - lastObstacleSpawn > (1500 + Math.random() * 2000) / speedMultiplier) {
                const obsWidth = 45 + Math.floor(Math.random() * 35);
                const obsHeight = 50 + Math.floor(Math.random() * 30);
                const imageCount = theme?.obstacleImages?.length || 0;
                
                currentObstacles.push({
                    id: Date.now(),
                    x: gameWidth + 50,
                    width: obsWidth,
                    height: obsHeight,
                    imageIndex: imageCount > 0 ? Math.floor(Math.random() * imageCount) : -1
                });
                lastObstacleSpawn = time;
            }

            // Colisiones AABB (Cajas de colisión con margen interno para píxeles transparentes)
            let collided = false;
            for (let obs of currentObstacles) {
                // Reducimos la hitbox del obstáculo quitándole un 20% a cada lado
                const obsMarginX = obs.width * 0.20;
                const obsMarginY = obs.height * 0.15;

                const obsLeft = obs.x + obsMarginX;
                const obsRight = obs.x + obs.width - obsMarginX;
                const obsBottom = groundHeight;
                const obsTop = groundHeight + obs.height - obsMarginY;

                // Reducimos la hitbox del jugador un 15% para que sea más permisivo
                const playerMargin = playerSize * 0.15;
                const playerLeft = playerX + playerMargin;
                const playerRight = playerX + playerSize - playerMargin;
                const playerBottom = groundHeight + characterYRef.current + playerMargin;
                const playerTop = groundHeight + characterYRef.current + playerSize - playerMargin;

                if (
                    playerRight > obsLeft &&
                    playerLeft < obsRight &&
                    playerTop > obsBottom &&
                    playerBottom < obsTop
                ) {
                    collided = true;
                    break;
                }
            }

            obstaclesRef.current = currentObstacles;
            setObstacles(currentObstacles);

            if (collided) {
                handleGameOver(false); // Chocó = Derrota
            } else {
                animationFrameId.current = requestAnimationFrame(loop);
            }
        };

        animationFrameId.current = requestAnimationFrame(loop);
    };

    const triggerJump = () => {
        if (!isJumpingRef.current && gameStateRef.current === 'playing') {
            isJumpingRef.current = true;
            jumpVelocityRef.current = jumpPower;
        }
    };

    const handleGameOver = async (didTimeExpireAndWon = false) => {
        setGameState('gameover');
        gameStateRef.current = 'gameover';
        if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current);

        const finalScore = Math.floor(scoreRef.current);

        if (finalScore > hiScore) {
            setHiScore(finalScore);
            localStorage.setItem('dinosaur_hi_score', finalScore.toString());
        }

        try {
            // Se gana si se supera el tiempo configurado o si se superan los 500 puntos en modo infinito
            const hasWon = didTimeExpireAndWon || (config?.playMode === 1 && finalScore >= 500);
            
            await fetch(`${process.env.REACT_APP_API_URL}Dinosaur/GameOver/${screenId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    targetScreenId: screenId,
                    playerName: currentPlayerName || 'Aficionado',
                    playerContact: isStandalone ? 'USB Antiestrés' : 'Celular',
                    score: finalScore,
                    hasWon: hasWon,
                    themeName: theme?.name || 'Corona'
                })
            });
        } catch (e) {
            console.error("Fallo al guardar récord:", e);
        }
    };

    const formatScore = (num) => {
        return num.toString().padStart(5, '0');
    };

    const fontColor = theme?.textColors || '#333333';
    const borderColor = theme?.borderColors || '#333333';
    
    const frameBackgroundStyle = {
        backgroundColor: theme?.gameFrameBackgroundType === 'color' ? theme.gameFrameBackgroundColor : '#f7f9fc',
        backgroundImage: theme?.gameFrameBackgroundType === 'image' && theme.gameFrameBackgroundUrl ? `url(${getMediaUrl(theme.gameFrameBackgroundUrl)})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        border: `4px solid ${borderColor}`
    };

    const containerScale = config?.scale ? config.scale / 100 : 1;

    return (
        <>
            {/* Fondo dinámico a pantalla completa (Igual a SlotMachine) */}
            <div className='SelfieFan__Screen__QR__Background'>
                {theme?.fanBackgroundType === 'image' && theme?.fanBackgroundUrl ? (
                    <img 
                        src={getMediaUrl(theme.fanBackgroundUrl)} 
                        className='SelfieFan__Screen__Selfie__Background__Image' 
                        alt="Background" 
                    />
                ) : (
                    <div 
                        className='SelfieFan__Screen__Selfie__Background__Image' 
                        style={{ backgroundColor: theme?.fanBackgroundColor || '#05050a' }}
                    />
                )}
            </div>

            <div 
                className="d-flex flex-column align-items-center justify-content-center position-relative" //w-100 h-100
                style={{
                    height: '100vh',
                    width: '100vw',
                    fontFamily: "'Press Start 2P', cursive",
                    userSelect: 'none',
                    overflow: 'hidden',
                    zIndex: 10
                }}
            >
                {/* Logo de la Marca Superior (Posicionado de forma absoluta arriba para no empujar el juego) */}
                {theme?.brandLogoUrl && (
                    <div className="position-absolute top-0 mt-4 text-center" style={{ transform: `scale(${containerScale})`, transformOrigin: 'top center', transition: 'transform 0.2s', zIndex: 15 }}>
                        <img src={getMediaUrl(theme.brandLogoUrl)} alt="Brand Logo" style={{ maxHeight: '80px', objectFit: 'contain' }} />
                    </div>
                )}

                {/* Contenedor padre centrado para control óptimo de Escala CSS (Scale) - Centrado en viewport */}
                <div 
                    className="d-flex align-items-center justify-content-center flex-grow-1 w-100"
                    style={{
                        transform: `scale(${containerScale})`,
                        transformOrigin: 'center center',
                        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                >
                {/* Cuadro de juego */}
                <div 
                    id="game-container" 
                    className="position-relative overflow-hidden rounded-3 shadow-lg"
                    style={{
                        width: `${gameWidth}px`,
                        height: `${gameHeight}px`,
                        ...frameBackgroundStyle
                    }}
                >
                    {/* Marcador */}
                    <div 
                        className="position-absolute fs-5" 
                        style={{ top: '20px', right: '20px', color: fontColor, zIndex: 30, fontSize: '16px' }}
                    >
                        HI {formatScore(hiScore)} {formatScore(score)}
                    </div>

                    {/* Mensaje de Inicio */}
                    {gameState === 'waiting' && (
                        <div 
                            className="position-absolute start-50 top-50 translate-middle text-center"
                            style={{ color: fontColor, zIndex: 30, fontSize: '13px', lineHeight: 1.6 }}
                        >
                            PRESIONA EL BOTÓN<br />PARA JUGAR
                        </div>
                    )}

                    {/* GAME OVER Screen */}
                    {gameState === 'gameover' && (
                        <div 
                            className="position-absolute start-50 top-50 translate-middle text-center p-4 border-0 rounded-3"
                            style={{ zIndex: 40, border: `4px solid ${borderColor}` }}
                        >
                            <h2 className="text-danger fw-bold" style={{ fontSize: '40px' }}>GAME OVER</h2>
                        </div>
                    )}

                    {/* Suelo */}
                    <div 
                        id="ground" 
                        className="position-absolute bottom-0 w-100" 
                        style={{ height: `${groundHeight}px`, backgroundColor: borderColor, zIndex: 10 }}
                    />

                    {/* Personaje */}
                    <div 
                        id="player" 
                        className="position-absolute"
                        style={{ 
                            left: `${playerX}px`, 
                            bottom: `${groundHeight + characterY}px`,
                            width: `${playerSize}px`,
                            height: `${playerSize}px`,
                            zIndex: 20
                        }}
                    >
                        {theme?.characterImage ? (
                            <img 
                                src={getMediaUrl(theme.characterImage)} 
                                alt="Player" 
                                className="w-100 h-100 object-fit-contain" 
                            />
                        ) : (
                            <div 
                                className="w-100 h-100 rounded border-3" 
                                style={{ backgroundColor: fontColor, borderColor: fontColor }} 
                            />
                        )}
                    </div>

                    {/* Obstáculos */}
                    {obstacles.map(obs => (
                        <div
                            key={obs.id}
                            className="position-absolute"
                            style={{
                                left: `${obs.x}px`,
                                bottom: `${groundHeight}px`,
                                width: `${obs.width}px`,
                                height: `${obs.height}px`,
                                zIndex: 15
                            }}
                        >
                            {obs.imageIndex >= 0 && theme?.obstacleImages ? (
                                <img 
                                    src={getMediaUrl(theme.obstacleImages[obs.imageIndex])} 
                                    alt="Obstacle" 
                                    className="w-100 h-100 object-fit-contain" 
                                />
                            ) : (
                                <div 
                                    className="w-100 h-100" 
                                    style={{ backgroundColor: borderColor, borderRadius: '50% 50% 0 0' }} 
                                />
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Indicador de Conexión en Standalone */}
            {isStandalone && (
                <div className="position-fixed bottom-0 end-0 p-3 small text-muted">
                    Conexión: <span className={connectionStatus === 'connected' ? 'text-success' : 'text-danger'}>{connectionStatus}</span>
                </div>
            )}

            {/* Pie de créditos corporativos */}
            <p className='text-center FooterCredits text-white py-3 mb-0 fw-bold' style={{ position: 'fixed', bottom: '15px', left: '50%', transform: 'translateX(-50%)', fontSize: '28px', zIndex: 12 }}>
                Hecho con ♥ por Interaktia
            </p>
        </div>
        </>
    );
};
